import type {
  LegacyDestination,
  LegacyDraftItem,
  LegacyEquipment,
  LegacyOrderDraft,
  LegacyProductItem,
  LegacyWebviewOrderDraft,
} from '../model/schema';

/**
 * Draft construction and validation for the order-create screen. Pure, so the
 * rules can be tested without mounting the form.
 */

/** Cargo category id the old FORM seeds ("other"). */
const DEFAULT_PRODUCT_TYPE_ID = 0;

/**
 * Cargo category id actually SENT. The old panel seeds its form with 0 but its
 * payload builders hardcode `type_id: 1` on both the origin and destination
 * entries (`index.tsx:252`, `index.tsx:288`), so 1 is what every order has ever
 * carried. Sending the form's 0 instead misdeclares the cargo category.
 */
const WIRE_PRODUCT_TYPE_ID = 1;

/** Backend stage returned by `eval` when the route leaves the delivery zone. */
export const STAGE_OUT_OF_ZONE = 28;

/**
 * Order stage from which the EDIT screen locks the cargo list: fields
 * disabled, per-item delete and "+ Продукт" hidden (old
 * `DestinationProductItem.tsx:25`, `DestinationProductBox.tsx:139`). By stage
 * 10 the courier has the goods, so amending the manifest would lie about what
 * is physically in the box.
 */
export const PRODUCTS_LOCK_STAGE = 10;

/** A stable id for form rows. `crypto.randomUUID` exists in every target env. */
export function newDraftId(): string {
  return globalThis.crypto.randomUUID();
}

/**
 * A blank cargo line, matching the old `emptyProductItem` — plus the generated
 * `id` the assignment picker addresses it by.
 *
 * Numeric fields start at 0 rather than undefined: the inputs render `0` as an
 * empty box, and `buildDraft` converts blanks per mode (create sends 0, edit
 * omits — the old panel's own asymmetry).
 */
export function emptyProductItem(): LegacyDraftItem {
  return {
    id: newDraftId(),
    name: '',
    price_per_unit: 0,
    quantity: 0,
    weight: 0,
    height: 0,
    width: 0,
    length: 0,
  };
}

/**
 * An empty stop, matching the old `emptyDestination`.
 *
 * `clientName` seeds the contact-name input with the old form's PLACEHOLDER
 * VALUE ("Отправитель"/"Получатель") — the old `DestinationBox` writes the
 * translated placeholder into the field itself when it is blank
 * (`DestinationBox.tsx:49-58`), and select-all-on-focus makes it replaceable
 * in one keystroke. It is a real value, so it passes the name requirement.
 */
export function emptyDestination(
  type: 'origin' | 'destination',
  order: number,
  productDescription: string,
  clientName = '',
): LegacyDestination {
  return {
    id: newDraftId(),
    arrivedAt: null,
    // A stop with no coordinate yet — `isDraftComplete` rejects these, and the
    // address picker fills them in. Kept as 0/0 rather than null because the
    // wire type is a plain number pair.
    location: { long: 0, lat: 0 },
    order,
    address: '',
    entrance: '',
    door_phone: '',
    floor: '',
    apartment: '',
    comment: '',
    client: { phone: '', name: clientName, email: '' },
    products: {
      type_id: DEFAULT_PRODUCT_TYPE_ID,
      description: productDescription,
      items: [],
    },
    type,
  };
}

/** The starting draft: one pickup, one drop-off — the old form's initial state. */
export function emptyDraft(
  productDescription: string,
  originClientName = '',
  destinationClientName = '',
): LegacyDestination[] {
  return [
    emptyDestination('origin', 0, productDescription, originClientName),
    emptyDestination('destination', 1, productDescription, destinationClientName),
  ];
}

/**
 * Renumbers `order` to match array position. Every mutation of the stop list
 * runs through this: the backend follows `order`, not array index, so an
 * insert or remove that skips it silently sends the courier out of sequence.
 */
export function resequence(destinations: LegacyDestination[]): LegacyDestination[] {
  return destinations.map((d, index) => ({ ...d, order: index }));
}

/**
 * Inserts a stop directly after the last one of the same kind, so pickups stay
 * grouped ahead of drop-offs — the order the courier drives and the order the
 * old screen rendered.
 */
export function addDestination(
  destinations: LegacyDestination[],
  type: 'origin' | 'destination',
  productDescription: string,
  clientName = '',
): LegacyDestination[] {
  const lastOfType = destinations.reduce(
    (last, d, index) => (d.type === type ? index : last),
    -1,
  );
  const at = lastOfType + 1;
  const next = [...destinations];
  next.splice(at, 0, emptyDestination(type, at, productDescription, clientName));
  return resequence(next);
}

/**
 * Removes a stop, refusing to drop the last pickup or the last drop-off — an
 * order needs at least one of each, and the API rejects fewer than two stops.
 *
 * Items assigned to a removed DROP-OFF revert to unassigned, so the products
 * box re-offers them rather than silently sending them to a stop that no
 * longer exists.
 */
export function removeDestination(
  destinations: LegacyDestination[],
  index: number,
): LegacyDestination[] {
  const target = destinations[index];
  if (!target) return destinations;

  const remaining = destinations.filter((d) => d.type === target.type).length;
  if (remaining <= 1) return destinations;

  const kept = destinations.filter((_, i) => i !== index);
  const orphaned =
    target.type === 'destination'
      ? kept.map((d) =>
          d.type === 'origin'
            ? {
                ...d,
                products: {
                  ...d.products,
                  items: d.products.items.map((item) =>
                    item.destinationIndex === target.id ? stripAssignment(item) : item,
                  ),
                },
              }
            : d,
        )
      : kept;

  return resequence(orphaned);
}

/** `destinationIndex` removed entirely — exactOptionalPropertyTypes-safe. */
function stripAssignment(item: LegacyDraftItem): LegacyDraftItem {
  const rest = { ...item };
  delete rest.destinationIndex;
  return rest;
}

/**
 * The drop-off a freshly added item is auto-assigned to: with exactly ONE
 * drop-off there is nothing to choose, so the old form assigns it immediately
 * (`DestinationProductBox.tsx:150-165`). With several, assignment is the
 * merchant's explicit pick and this returns null.
 */
export function autoAssignTargetId(destinations: LegacyDestination[]): string | null {
  const dropOffs = destinations.filter((d) => d.type === 'destination');
  return dropOffs.length === 1 ? (dropOffs[0]?.id ?? null) : null;
}

/** Adds a blank item to the pickup at `originIndex`, auto-assigning if possible. */
export function addProductItem(
  destinations: LegacyDestination[],
  originIndex: number,
): LegacyDestination[] {
  const target = destinations[originIndex];
  if (target?.type !== 'origin') return destinations;

  const auto = autoAssignTargetId(destinations);
  const item = auto
    ? { ...emptyProductItem(), destinationIndex: auto }
    : emptyProductItem();

  return destinations.map((d, i) =>
    i === originIndex
      ? { ...d, products: { ...d.products, items: [...d.products.items, item] } }
      : d,
  );
}

/** Assigns item `itemId` (on any pickup) to the drop-off stop `stopId`. */
export function assignItem(
  destinations: LegacyDestination[],
  itemId: string,
  stopId: string,
): LegacyDestination[] {
  return destinations.map((d) =>
    d.type === 'origin'
      ? {
          ...d,
          products: {
            ...d.products,
            items: d.products.items.map((item) =>
              item.id === itemId ? { ...item, destinationIndex: stopId } : item,
            ),
          },
        }
      : d,
  );
}

/** Returns item `itemId` to the unassigned pool. */
export function unassignItem(
  destinations: LegacyDestination[],
  itemId: string,
): LegacyDestination[] {
  return destinations.map((d) =>
    d.type === 'origin'
      ? {
          ...d,
          products: {
            ...d.products,
            items: d.products.items.map((item) =>
              item.id === itemId ? stripAssignment(item) : item,
            ),
          },
        }
      : d,
  );
}

/** Every pickup item not yet assigned to a drop-off — the chip row's source. */
export function unassignedItems(destinations: LegacyDestination[]): LegacyDraftItem[] {
  return destinations
    .filter((d) => d.type === 'origin')
    .flatMap((d) => d.products.items)
    .filter((item) => !item.destinationIndex);
}

/** The pickup items assigned to drop-off `stopId` — its read-only manifest. */
export function itemsAssignedTo(
  destinations: LegacyDestination[],
  stopId: string,
): LegacyDraftItem[] {
  return destinations
    .filter((d) => d.type === 'origin')
    .flatMap((d) => d.products.items)
    .filter((item) => item.destinationIndex === stopId);
}

/** A stop is usable once it has a real coordinate and an address. */
export function isDestinationComplete(destination: LegacyDestination): boolean {
  const { long, lat } = destination.location;
  return destination.address.trim() !== '' && (long !== 0 || lat !== 0);
}

/**
 * Whether the draft can be PRICED — a strictly weaker condition than being
 * submittable.
 *
 * The old screen re-runs `POST /orders/eval` as soon as a vehicle is chosen and
 * every stop has coordinates; it deliberately does NOT wait for phones or
 * cargo. That ordering is the point: the merchant sees what the delivery costs
 * while filling the form, not after finishing it.
 *
 * Gating the price on `isDraftComplete` instead leaves it blank until the last
 * recipient phone is typed, which reads as "the price doesn't calculate".
 */
export function isDraftPriceable(
  destinations: LegacyDestination[],
  equipmentId: number | null,
): boolean {
  if (equipmentId === null) return false;
  if (destinations.length < 2) return false;
  return destinations.every((d) => d.location.long !== 0 || d.location.lat !== 0);
}

/** E.164 length for `+998` + nine national digits. */
const COMPLETE_PHONE_LENGTH = 13;

/**
 * What is wrong with a draft at submit time, in the categories the old screen
 * reported them (each has its own toast/marker — see the page's submit
 * handler):
 *
 * - `unassignedItems` — a pickup item with no drop-off assigned; the old form
 *   raised its `product_select` error, painted the products box red and
 *   force-opened it (`create/index.tsx:352-367`).
 * - `missingFields` — the old form's RHF `required`s: address, contact name,
 *   contact phone, cargo description, and name/price/quantity on each item.
 * - `shortPhone` — present but incomplete phone; a separate toast in the old
 *   submit (`toast.client_field_error`).
 * - `missingCoords` — a stop with no coordinate (`create.empty_coords_error`).
 */
export interface DraftIssues {
  unassignedItems: boolean;
  missingFields: boolean;
  shortPhone: boolean;
  missingCoords: boolean;
}

export function draftIssues(destinations: LegacyDestination[]): DraftIssues {
  const missingFields = destinations.some(
    (d) =>
      d.address.trim() === '' ||
      d.client.name.trim() === '' ||
      d.client.phone === '' ||
      d.products.description.trim() === '' ||
      d.products.items.some(
        (item) =>
          item.name.trim() === '' || item.quantity <= 0 || item.price_per_unit <= 0,
      ),
  );

  return {
    unassignedItems: unassignedItems(destinations).length > 0,
    missingFields,
    shortPhone: destinations.some(
      (d) => d.client.phone !== '' && d.client.phone.length < COMPLETE_PHONE_LENGTH,
    ),
    missingCoords: destinations.some(
      (d) => d.location.long === 0 && d.location.lat === 0,
    ),
  };
}

/**
 * Whether the draft can be submitted.
 *
 * Transcribed from the old `areAllRequiredFieldsFilled`, which requires on
 * EVERY stop: a non-empty address, a contact NAME (the old edit submit
 * rejected a blank one outright, `edit/index.tsx:574-584`), a full phone
 * (`phone.length === 13`), and — when the stop carries items — a name,
 * quantity, price AND a drop-off assignment on each
 * (`CreateBody.tsx:155-161`).
 *
 * The additional coordinate check is ours: the old form kept coordinates in a
 * parallel field that its own validator never inspected, but the API rejects a
 * stop without one, so requiring it here turns a server error into a disabled
 * button.
 */
export function isDraftComplete(
  destinations: LegacyDestination[],
  equipmentId: number | null,
): boolean {
  if (equipmentId === null) return false;
  if (destinations.length < 2) return false;

  return destinations.every((destination) => {
    if (!isDestinationComplete(destination)) return false;
    if (destination.client.phone.length !== COMPLETE_PHONE_LENGTH) return false;
    if (destination.client.name.trim() === '') return false;

    // "What are we carrying" is REQUIRED on every stop — the source's
    // `isValidProductDescription` rejects a blank or whitespace-only value
    // (`CreateBody.tsx:165-172`). It is pre-filled with "Другое", so this only
    // bites when a merchant clears it.
    if (destination.products.description.trim() === '') return false;

    // Items are optional, but a half-filled or unassigned item is not — the
    // source requires `destinationIndex` on every item (`CreateBody.tsx:160`).
    return destination.products.items.every(
      (item) =>
        item.name.trim() !== '' &&
        item.quantity > 0 &&
        item.price_per_unit > 0 &&
        (destination.type !== 'origin' || Boolean(item.destinationIndex)),
    );
  });
}

/** Which flow a wire body is being built for — the rules differ per mode. */
export type DraftBuildMode = 'create' | 'edit' | 'copy';

/** Form item → wire item. Blank numbers: create sends 0, edit/copy omit. */
function toWireItem(item: LegacyDraftItem, mode: DraftBuildMode): LegacyProductItem {
  const blankable = (value: number) =>
    mode === 'create' ? value : value > 0 ? value : undefined;

  return {
    name: item.name,
    price_per_unit: item.price_per_unit,
    // `quantity` follows the same rule as the dimensions: the old edit builder
    // sends `undefined` for a blank (`edit/index.tsx:457-459`).
    quantity: blankable(item.quantity),
    weight: blankable(item.weight),
    height: blankable(item.height),
    width: blankable(item.width),
    length: blankable(item.length),
    // Carried through on the full-body PATCH — omitting it deletes the photo.
    ...(item.photo !== undefined ? { photo: item.photo } : {}),
  };
}

/**
 * Assembles the wire payload from form state.
 *
 * The screen keeps ONE ordered list of stops; the API takes TWO arrays. The
 * split happens here, and each stop's `order` is its 1-BASED position in the
 * combined list — which is the only thing that reunites the two arrays into
 * the sequence the courier drives. Numbering per-array instead would send two
 * stops both claiming position 1.
 *
 * `floor` is also converted here: the form holds it as text, the wire needs a
 * number or null (see `legacyWireDestinationSchema`).
 *
 * Mode-dependent rules, each the old panel's own:
 *
 * - `create` → `vendor_order_id: '2'`, `is_business: true`, `type: 'EXPRESS'`
 *   always (`create/index.tsx:232-233,320`), blanks in items sent as 0.
 * - `edit`/`copy` → the ORDER's `vendor_order_id`/`is_business` echoed
 *   (`edit/index.tsx:428-429`), `type: 'DELAYED'` when scheduled
 *   (`edit/index.tsx:539`), blanks omitted, server leg ids echoed
 *   (`edit/index.tsx:476,531`).
 */
export function buildDraft(params: {
  destinations: LegacyDestination[];
  equipmentId: number;
  paymentType: string;
  doorToDoor: boolean;
  sendLink: boolean;
  productPaid: boolean;
  /** ISO timestamp for a scheduled pickup, or null for "as soon as possible". */
  scheduledAt: string | null;
  mode?: DraftBuildMode;
  /** EDIT/COPY: the placed order's value, echoed verbatim. */
  vendorOrderId?: string;
  isBusiness?: boolean;
}): LegacyOrderDraft {
  const mode: DraftBuildMode = params.mode ?? 'create';

  const toWireStop = (d: LegacyDestination, position: number) => ({
    // The server leg id is echoed so the PATCH amends the existing leg; a new
    // stop sends none and the backend mints one.
    ...(mode !== 'create' && d.serverId !== undefined ? { id: d.serverId } : {}),
    location: d.location,
    order: position + 1,
    address: d.address,
    entrance: d.entrance,
    door_phone: d.door_phone,
    // `Number('') || null` → null, matching the old form's
    // `Number(el.floor) || undefined`. A blank or unparseable floor must NOT
    // go as `''`: the backend 400s on it and the whole price preview fails.
    floor: Number(d.floor) || null,
    apartment: d.apartment,
    comment: d.comment,
    client: d.client,
    products: {
      type_id: WIRE_PRODUCT_TYPE_ID,
      description: d.products.description,
      items:
        d.type === 'origin'
          ? d.products.items.map((item) => toWireItem(item, mode))
          : // A drop-off receives EXACTLY the items assigned to it — the old
            // builders copy each origin item onto the destination its
            // `destinationIndex` names (`create/index.tsx:290-311`). Without
            // this the courier's app receives items it cannot attribute to
            // any stop.
            itemsAssignedTo(params.destinations, d.id).map((item) =>
              toWireItem(item, mode),
            ),
    },
    type: d.type,
  });

  const ordered = params.destinations.map((d, index) => toWireStop(d, index));

  return {
    vendor_order_id: mode === 'create' ? '2' : (params.vendorOrderId ?? '2'),
    is_business: mode === 'create' ? true : (params.isBusiness ?? true),
    origin: ordered.filter((d) => d.type === 'origin'),
    destination: ordered.filter((d) => d.type === 'destination'),
    payment_type: params.paymentType,
    delivery: {
      door_to_door: params.doorToDoor,
      equipment_id: params.equipmentId,
      // The old CREATE screen sends EXPRESS even for a scheduled order; only
      // the edit screen distinguishes DELAYED (`edit/index.tsx:539`).
      type: mode === 'create' ? 'EXPRESS' : params.scheduledAt ? 'DELAYED' : 'EXPRESS',
      send_link: params.sendLink,
      product_paid: params.productPaid,
      time: params.scheduledAt,
    },
  };
}

/**
 * The CREATE-WEBVIEW body (`create-webview/index.tsx:144-224`), reproduced
 * quirk for quirk:
 *
 * - `order` numbered PER ARRAY (`ind + 1` for pickups, `ind + 2` for
 *   drop-offs) rather than across the merged route;
 * - origin entries carry NO products block at all;
 * - each drop-off sends ITS OWN `products.items` verbatim — not the
 *   assignment-derived list the main screens send;
 * - `payment_type` is always BALANCE, there is no `is_business`, and
 *   `delivery` carries no `product_paid`.
 *
 * One divergence: the old screen's `time` expression evaluated
 * `dayjs(undefined)` at midnight whenever its `dateDrawer` state was unset,
 * silently scheduling every unscheduled order for today 00:00. Here an
 * unscheduled order sends `null`, the value every other screen sends.
 */
export function buildWebviewDraft(params: {
  destinations: LegacyDestination[];
  equipmentId: number;
  doorToDoor: boolean;
  sendLink: boolean;
  scheduledAt: string | null;
}): LegacyWebviewOrderDraft {
  const base = (d: LegacyDestination) => ({
    location: d.location,
    address: d.address,
    entrance: d.entrance,
    door_phone: d.door_phone,
    floor: Number(d.floor) || null,
    apartment: d.apartment,
    comment: d.comment,
    client: d.client,
  });

  const origins = params.destinations.filter((d) => d.type === 'origin');
  const dropOffs = params.destinations.filter((d) => d.type === 'destination');

  return {
    vendor_order_id: '2',
    origin: origins.map((d, ind) => ({ ...base(d), order: ind + 1 })),
    destination: dropOffs.map((d, ind) => ({
      ...base(d),
      order: ind + 2,
      products: {
        type_id: WIRE_PRODUCT_TYPE_ID,
        description: d.products.description,
        items: d.products.items.map((item) => toWireItem(item, 'create')),
      },
    })),
    payment_type: 'BALANCE',
    delivery: {
      door_to_door: params.doorToDoor,
      equipment_id: params.equipmentId,
      send_link: params.sendLink,
      type: 'EXPRESS',
      time: params.scheduledAt,
    },
  };
}

/** Grams → kilograms, for the equipment card's capacity line. */
const GRAMS_PER_KG = 1000;

/** The unit labels the spec line renders, localized by the caller. */
export interface EquipmentSpecUnits {
  sm: string;
  to: string;
  kg: string;
  km: string;
}

/** Fallback (the pre-localization hardcodes), so tests stay legible. */
const DEFAULT_UNITS: EquipmentSpecUnits = { sm: 'cm', to: 'to', kg: 'kg', km: 'km' };

/**
 * `43×43×43см до 15 кг - 8 км` — the equipment card's subtitle. The unit words
 * come from the `legacy` namespace (`sm`/`to`/`kg`/`km`), as the old card
 * localized them (`Tariffs.tsx:150-157`).
 */
export function equipmentSpec(
  equipment: LegacyEquipment,
  units: EquipmentSpecUnits = DEFAULT_UNITS,
): string {
  const kg = equipment.max_weight / GRAMS_PER_KG;
  return `${equipment.width}×${equipment.height}×${equipment.length}${units.sm} ${units.to} ${kg} ${units.kg} - ${equipment.max_range} ${units.km}`;
}
